import { useState } from "react";
import { useChainId } from "wagmi";
import { motion } from "framer-motion";
import { base, sepolia } from "wagmi/chains";
import { type Address } from "viem";
import { WalletButton } from "./WalletButton";
import { DelegationModal } from "./DelegationModal";
import { ConnectorButtons } from "./ConnectorButtons";

// Delegate addresses per chain (kept in sync with apps/web/.env)
const DELEGATE: Record<number, Address> = {
  [base.id]: (import.meta.env.VITE_GAIA_DELEGATE_BASE ?? "0xB147331db2c6f894E7fAA5E50D6b7880438f833E") as Address,
  [sepolia.id]: (import.meta.env.VITE_GAIA_DELEGATE_SEPOLIA ?? "0x9270C41C4857D420203A6B0F2c1aC5cab7614B14") as Address,
};

export function DelegationSteps() {
  const rawChainId = useChainId();
  const chainId = typeof rawChainId === "string" ? parseInt(rawChainId, 10) : rawChainId;
  const targetChainId = DELEGATE[chainId ?? 0] ? chainId : base.id;
  const delegateAddr = DELEGATE[targetChainId ?? base.id];
  const chainName = targetChainId === base.id ? "Base" : "Sepolia";
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section id="ai-mint" className="relative px-5 md:px-10 py-24 md:py-32 max-w-7xl mx-auto">
      <div className="max-w-3xl mb-12">
        <div className="eyebrow mb-4"><span className="dot" />ai-only mint</div>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
          three steps. <span className="font-serif italic text-aurora">one signature.</span>
        </h2>
        <p className="mt-6 text-bone-dim text-base max-w-2xl">
          GAIA can't be minted from this page. You delegate your EOA to our minting contract
          once via <span className="font-mono text-bone">EIP-7702</span>, then any AI agent
          with the MCP can mint on your behalf.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <Step
          n={1}
          title="connect wallet"
          tag="wallet"
          body={
            <>
              <p className="text-bone-dim">
                Connect with MetaMask, Coinbase, DogeShit, or any EIP-6963 wallet.
                We auto-switch to {chainName}.
              </p>
              <div className="mt-4">
                <WalletButton />
              </div>
            </>
          }
        />
        <Step
          n={2}
          title="sign delegation"
          tag="EIP-7702"
          body={
            <>
              <p className="text-bone-dim">
                Sign one authorization pointing your EOA at the GaiaDelegate contract.
                This is your only on-chain action.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => setModalOpen(true)}
                  className="btn btn-primary w-full justify-center"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mr-2">
                    <path d="M3 8 H13 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  open delegation
                </button>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-bone-dim text-center">
                  {delegateAddr.slice(0, 10)}…{delegateAddr.slice(-8)}
                </div>
              </div>
            </>
          }
        />
      </div>

      {/* Step 3 — full-width connector panel */}
      <div className="mt-5">
        <Step
          n={3}
          title="ask claude to mint"
          tag="MCP"
          className="min-h-0"
          body={
            <>
              <p className="text-bone-dim max-w-2xl">
                Add the GAIA MCP to your AI agent. Then say:{" "}
                <span className="font-mono text-bone">"mint me some GAIA"</span>.
                The relayer pays gas; your EOA pays the 0.001 ETH fee.
              </p>
              <div className="mt-5">
                <ConnectorButtons />
              </div>
            </>
          }
        />
      </div>

      <DelegationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}

function Step({
  n, title, tag, body, className,
}: {
  n: number; title: string; tag: string;
  body: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1], delay: (n - 1) * 0.1 }}
      className={`ink-card p-6 md:p-7 flex flex-col gap-4 min-h-[280px] ${className ?? ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="size-10 rounded-full bg-gradient-to-br from-teal-300 to-leaf grid place-items-center font-mono text-ink-1000 font-bold">
          {n}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">
          {tag}
        </span>
      </div>
      <div className="font-display text-2xl md:text-3xl tracking-tight">{title}</div>
      <div className="flex-1">{body}</div>
    </motion.div>
  );
}
